import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '../Modal';
import PhotoPicker from '../PhotoPicker';
import PictureStudio from '../PictureStudio';
import Lightbox from '../Lightbox/Lightbox';
import { contentService } from '../../services/api';
import { brisbaneInputsToUtc, utcToBrisbaneInputs } from '../../utils/dateFormatters';
import { PILLARS, STATUS_LABEL, TWEAKS, quickTimes, whenLabel, seeMoreHook } from '../../utils/content';
import './PostEditor.css';

const EDITABLE = ['idea', 'draft', 'scheduled', 'failed'];
const PAGE_NAME = 'Get It Done - Efficient Home Solutions';

function formFrom(p) {
  const when = p.scheduled_for ? utcToBrisbaneInputs(p.scheduled_for) : { date: '', time: '' };
  return { title: p.title || '', pillar: p.pillar || '', caption: p.caption || '', date: when.date, time: when.time };
}

/**
 * One Facebook post (Content Studio, backlog #780, GID-Session-025): its photos,
 * its words (with the AI to write or rework them), when it goes out, and the
 * sign-off. Closing saves unsaved changes. Approving is the per-post OK from
 * Andrew or Leanne; after that it goes out on its own at the time set.
 */
export default function PostEditor({ postId, onClose, onChanged, fbMode = 'off', pictures = null, aiReady = false }) {
  const [post, setPost] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pictureOpen, setPictureOpen] = useState(null); // { scene } while the picture studio is open
  const [ai, setAi] = useState({ loading: false, variants: [], warnings: [], error: '', ask: '' });
  const [lightbox, setLightbox] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const fileRef = useRef(null);
  const captionRef = useRef(null);

  const showPost = useCallback((p, { keepForm = false } = {}) => {
    setPost(p);
    if (!keepForm) { setForm(formFrom(p)); setDirty(false); }
  }, []);

  useEffect(() => {
    let live = true;
    contentService.get(postId).then((r) => {
      if (!live) return;
      if (r.success) showPost(r.post); else setError(r.error);
    });
    return () => { live = false; };
  }, [postId, showPost]);

  // While a post is going out, check back until it lands.
  useEffect(() => {
    if (post?.status !== 'publishing') return undefined;
    const t = setInterval(async () => {
      const r = await contentService.get(post.id);
      if (r.success && r.post.status !== 'publishing') { showPost(r.post); onChanged?.(); }
    }, 2500);
    return () => clearInterval(t);
  }, [post?.status, post?.id, showPost, onChanged]);

  if (!post || !form) {
    return (
      <Modal isOpen onClose={onClose} title="Post" size="full">
        {error ? <div className="form-error">{error}</div> : <div className="pe-loading"><div className="loading-spinner"></div></div>}
      </Modal>
    );
  }

  const editable = EDITABLE.includes(post.status);
  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setDirty(true); setError(''); };
  const whenIso = form.date && form.time ? brisbaneInputsToUtc(form.date, form.time) : null;

  // Returns the saved post (or the current one when there was nothing to save), null on failure.
  async function save({ quiet = false } = {}) {
    if (!dirty || !editable) return post;
    if (!quiet) setBusy('save');
    const r = await contentService.update(post.id, {
      title: form.title, pillar: form.pillar || null, caption: form.caption, scheduled_for: whenIso
    });
    if (!quiet) setBusy('');
    if (!r.success) { setError(r.error); return null; }
    showPost(r.post);
    onChanged?.();
    return r.post;
  }

  async function close() {
    if (dirty && editable && !(await save({ quiet: true }))) return; // keep it open to show the error
    onClose();
  }

  async function mediaCall(promise) {
    setBusy('media'); setError('');
    const r = await promise;
    setBusy('');
    if (!r.success) { setError(r.error); return false; }
    showPost(r.post, { keepForm: true });
    onChanged?.();
    return true;
  }

  async function addPhotos(ids) {
    if (await mediaCall(contentService.addPhotos(post.id, ids))) setPickerOpen(false);
  }
  async function addLibrary(ids) {
    if (await mediaCall(contentService.addPhotos(post.id, [], ids))) setPickerOpen(false);
  }
  async function beforeAfter(b, a) {
    if (await mediaCall(contentService.beforeAfter(post.id, b, a))) setPickerOpen(false);
  }
  function move(id, dir) {
    const ids = post.media.map((m) => m.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    mediaCall(contentService.reorder(post.id, ids));
  }
  const removeMedia = (id) => mediaCall(contentService.deleteMedia(id));
  // A picture from the studio goes on the post (its own copy, labelled "Made with AI").
  async function putPicture(picture) {
    if (await mediaCall(contentService.addPicture(post.id, picture.id))) setPictureOpen(null);
  }
  async function openPictures(scene = '') {
    if (dirty && !(await save({ quiet: true }))) return; // the AI reads the saved words
    setPictureOpen({ scene });
  }
  const upload = (files) => files.length && mediaCall(contentService.upload(post.id, files));

  // A before and an after already on the post (job photos or library photos) can be combined.
  const realPhoto = (m) => (m.source === 'job_photo' || m.source === 'library') && !m.missing;
  const befores = post.media.filter((m) => realPhoto(m) && m.photo_kind === 'before');
  const afters = post.media.filter((m) => realPhoto(m) && m.photo_kind === 'after');
  const refOf = (m) => (m.source === 'library' ? { library: m.library_id } : { job: m.appointment_image_id });
  const refLabel = (m) => (m.source === 'library' ? `L${m.library_id}` : `#${m.appointment_image_id}`);
  const hasMade = post.media.some((m) => m.source === 'made');

  async function runAi(tweak = '') {
    if (!(await save({ quiet: true }))) return;
    setAi((s) => ({ ...s, loading: true, error: '', variants: [], warnings: [] }));
    const r = await contentService.pollAi(contentService.captions(post.id, tweak));
    if (!r.success) return setAi((s) => ({ ...s, loading: false, error: r.error }));
    setAi((s) => ({ ...s, loading: false, variants: r.result.variants, warnings: r.result.warnings }));
  }

  function pickVariant(v) {
    set('caption', v.caption);
    captionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  // Save first: a new name gives the post its tracking tag, and the link should carry it.
  async function insertLink() {
    const el = captionRef.current;
    const at = el ? el.selectionStart : form.caption.length;
    const saved = await save({ quiet: true });
    if (!saved) return;
    const text = form.caption;
    const before = text.slice(0, at);
    const pad = before && !before.endsWith('\n') && !before.endsWith(' ') ? ' ' : '';
    set('caption', `${before}${pad}${saved.quote_link}${text.slice(at)}`);
  }

  async function approve(now = false) {
    setError(''); setConfirm('');
    if (!form.caption.trim()) return setError('Write the post first: it has no words');
    if (!now && !whenIso) return setError('Pick a day and time for it to go out');
    if (!now && new Date(whenIso).getTime() < Date.now()) return setError('That time has passed: pick a later one, or Post now');
    if (!(await save({ quiet: true }))) return;
    setBusy(now ? 'now' : 'approve');
    const r = await contentService.approve(post.id, now ? { now: true } : { at: whenIso });
    setBusy('');
    if (!r.success) return setError(r.error);
    showPost(r.post);
    onChanged?.();
  }

  async function simple(kind, call) {
    setBusy(kind); setError(''); setConfirm('');
    const r = await call;
    setBusy('');
    if (!r.success) { setError(r.error); return null; }
    if (r.post) showPost(r.post);
    if (r.note) setNote(r.note);
    onChanged?.();
    return r;
  }

  async function remove() {
    const r = await simple('delete', contentService.delete(post.id));
    if (r) onClose();
  }

  const statusChip = <span className={`pe-status status-${post.status}`}>{STATUS_LABEL[post.status] || post.status}{post.simulated ? ' (simulated)' : ''}</span>;
  const hook = seeMoreHook(form.caption);
  const longCaption = form.caption.length > 260 || form.caption.split('\n').length > 5;

  return (
    <Modal isOpen onClose={close} title={post.title || 'New post'} size="xl">
      <div className="post-editor" data-post={post.id} data-status={post.status}>
        <div className="pe-topline">
          {statusChip}
          {post.job && <span className="chip">From {post.job.job_number}{post.job.suburb ? ` · ${post.job.suburb}` : ''}</span>}
          {post.status === 'scheduled' && <span className="text-muted">Goes out {whenLabel(post.scheduled_for)}{post.approved_by_name ? ` · approved by ${post.approved_by_name}` : ''}</span>}
          {post.status === 'published' && <span className="text-muted">Posted {whenLabel(post.published_at)}</span>}
        </div>

        {post.status === 'idea' && (
          <div className="pe-callout">
            <strong>An idea from the AI.</strong> Make it a draft to put its photos on{post.ai?.make_before_after ? ' and make the before/after image' : ''}, then tweak it and approve it.
          </div>
        )}
        {post.status === 'failed' && <div className="pe-callout pe-callout-red"><strong>This one didn't go out.</strong> {post.last_error}</div>}
        {post.status === 'published' && post.simulated && (
          <div className="pe-callout">Simulated on this computer: nothing was sent to Facebook. On the live app this post would be on the page now.</div>
        )}
        {post.notes && <div className="pe-notes"><span className="text-muted">Notes:</span> {post.notes}</div>}
        {note && <div className="pe-callout">{note}</div>}

        <div className="pe-grid">
          {/* ---- left: details + photos ---- */}
          <div className="pe-col">
            <div className="form-row pe-row">
              <div className="form-group">
                <label className="form-label" htmlFor="pe-title">Name (only we see this)</label>
                <input id="pe-title" className="form-input" value={form.title} disabled={!editable} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Robina driveway, day 3" />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="pe-pillar">Type</label>
                <select id="pe-pillar" className="form-select" value={form.pillar} disabled={!editable} onChange={(e) => set('pillar', e.target.value)}>
                  <option value="">Not set</option>
                  {PILLARS.map((p) => <option key={p.value} value={p.value}>{p.label}{p.hint ? `: ${p.hint}` : ''}</option>)}
                </select>
              </div>
            </div>

            <div className="pe-section">
              <div className="pe-section-head">
                <h3>Photos <span className="text-muted">({post.media.length}/10)</span></h3>
                {editable && post.status !== 'idea' && (
                  <div className="pe-media-buttons">
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setPickerOpen(true)} disabled={busy === 'media'}>+ Photos</button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => fileRef.current?.click()} disabled={busy === 'media'}>+ Upload</button>
                    {pictures && <button type="button" className="btn btn-outline btn-sm" onClick={() => openPictures()} disabled={busy === 'media'}>✨ Picture</button>}
                    <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => { upload([...e.target.files]); e.target.value = ''; }} />
                  </div>
                )}
              </div>
              {post.media.length === 0 ? (
                <p className="text-muted pe-empty">{post.status === 'idea' ? `The idea picks ${post.ai?.photo_ids?.length || 'no'} photo${post.ai?.photo_ids?.length === 1 ? '' : 's'}${post.ai?.shot_needed ? `. Shot needed: ${post.ai.shot_needed}` : ''}.` : 'No photos: it goes out as a text post. Posts with a photo do much better.'}</p>
              ) : (
                <div className="pe-media">
                  {post.media.map((m, i) => (
                    <div key={m.id} className="pe-media-item" data-media={m.id}>
                      <button type="button" className="pe-media-img" onClick={() => setLightbox({ images: post.media.map((x) => ({ src: contentService.mediaUrl(x.id), label: x.label })), index: i })}>
                        <img src={contentService.mediaUrl(m.id, 'thumb')} alt={m.label} />
                        <span className={`pe-media-label${m.source === 'made' ? ' made' : m.source === 'ai' ? ' ai' : ''}`}>{i === 0 ? 'Lead · ' : ''}{m.label}</span>
                        {m.missing && <span className="pe-media-missing">Photo deleted</span>}
                      </button>
                      {editable && (
                        <div className="pe-media-tools">
                          <button type="button" title="Earlier" disabled={i === 0 || busy === 'media'} onClick={() => move(m.id, -1)}>◀</button>
                          <button type="button" title="Later" disabled={i === post.media.length - 1 || busy === 'media'} onClick={() => move(m.id, 1)}>▶</button>
                          <button type="button" title="Take it off this post" disabled={busy === 'media'} onClick={() => removeMedia(m.id)}>✕</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {post.ai?.picture && (
                <div className="pe-picture-idea">
                  <span><strong>Picture idea:</strong> {post.ai.picture}</span>
                  {editable && post.status !== 'idea' && pictures
                    ? <button type="button" className="btn btn-outline btn-sm" onClick={() => openPictures(post.ai.picture)}>✨ Make it</button>
                    : post.status === 'idea' ? <span className="text-muted">Make it a draft, then make the picture.</span> : null}
                </div>
              )}
              {editable && befores.length > 0 && afters.length > 0 && !hasMade && (
                <button type="button" className="btn btn-outline btn-sm pe-ba" disabled={busy === 'media'} onClick={() => beforeAfter(refOf(befores[0]), refOf(afters[0]))}>
                  Combine {refLabel(befores[0])} + {refLabel(afters[0])} into one before/after image
                </button>
              )}
            </div>

            {/* Facebook-style preview */}
            <div className="pe-section">
              <h3>Preview</h3>
              <div className="fb-preview">
                <div className="fb-head">
                  <span className="fb-avatar">GD</span>
                  <div><strong>{PAGE_NAME}</strong><span className="text-muted">{post.status === 'published' ? whenLabel(post.published_at) : whenIso ? whenLabel(whenIso) : 'Not scheduled'} · 🌏</span></div>
                </div>
                <div className="fb-text">
                  {longCaption && !expanded ? (
                    <>{form.caption.slice(0, 220).trimEnd()}… <button type="button" className="fb-more" onClick={() => setExpanded(true)}>See more</button></>
                  ) : (form.caption || <span className="text-muted">No words yet.</span>)}
                </div>
                {post.media.length > 0 && (
                  <div className={`fb-photos n${Math.min(post.media.length, 3)}`}>
                    {post.media.slice(0, 3).map((m, i) => (
                      <div key={m.id} className="fb-photo">
                        <img src={contentService.mediaUrl(m.id, i === 0 ? undefined : 'thumb')} alt={m.label} />
                        {i === 2 && post.media.length > 3 && <span className="fb-plus">+{post.media.length - 3}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ---- right: words + AI + when ---- */}
          <div className="pe-col">
            <div className="pe-section">
              <div className="pe-section-head">
                <h3>The words</h3>
                {editable && <button type="button" className="btn btn-link btn-sm" onClick={insertLink}>Insert quote link</button>}
              </div>
              <textarea id="pe-caption" ref={captionRef} className="form-input pe-caption" rows={12} value={form.caption} disabled={!editable}
                onChange={(e) => set('caption', e.target.value)} placeholder="What do we want to say? Or ask the AI below." />
              <div className="pe-caption-meta text-muted">
                <span>{form.caption.length} characters</span>
                {hook && <span className="pe-hook" title="Roughly what shows before See more">Hook: “{hook}”</span>}
              </div>
              <p className="pe-link text-muted">Quote link for this post: <code>{post.quote_link}</code></p>
            </div>

            {editable && post.status !== 'idea' && (
              <div className="pe-section pe-ai">
                <div className="pe-section-head">
                  <h3>✨ Creative partner</h3>
                  <button type="button" className="btn btn-primary btn-sm" disabled={ai.loading} onClick={() => runAi('')}>
                    {form.caption.trim() ? 'Give me 3 options' : 'Write it for me'}
                  </button>
                </div>
                {form.caption.trim() && (
                  <div className="pe-tweaks">
                    {TWEAKS.map((t) => <button key={t.label} type="button" className="pe-tweak" disabled={ai.loading} onClick={() => runAi(t.tweak)}>{t.label}</button>)}
                  </div>
                )}
                <form className="pe-ask" onSubmit={(e) => { e.preventDefault(); if (ai.ask.trim()) runAi(ai.ask.trim()); }}>
                  <input className="form-input" value={ai.ask} disabled={ai.loading} onChange={(e) => setAi((s) => ({ ...s, ask: e.target.value }))}
                    placeholder='Or tell it: "mention it took 3 days", "make the mould the villain"…' />
                  <button type="submit" className="btn btn-outline btn-sm" disabled={ai.loading || !ai.ask.trim()}>Go</button>
                </form>
                {ai.loading && <p className="pe-ai-wait"><span className="loading-spinner small"></span> Writing… usually 20–40 seconds.</p>}
                {ai.error && <div className="form-error">{ai.error}</div>}
                {ai.warnings.length > 0 && (
                  <ul className="pe-warnings">{ai.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}</ul>
                )}
                {ai.variants.map((v, i) => (
                  <div key={i} className="pe-variant">
                    <div className="pe-variant-head">
                      <strong>{v.label}</strong>
                      <button type="button" className="btn btn-outline btn-sm" onClick={() => pickVariant(v)}>Use this</button>
                    </div>
                    <p>{v.caption}</p>
                  </div>
                ))}
              </div>
            )}

            {editable && post.status !== 'idea' && (
              <div className="pe-section">
                <h3>When it goes out</h3>
                <div className="pe-when">
                  <input type="date" className="form-input" value={form.date} onChange={(e) => set('date', e.target.value)} aria-label="Day" />
                  <input type="time" className="form-input" value={form.time} onChange={(e) => set('time', e.target.value)} aria-label="Time" />
                </div>
                <div className="pe-quick">
                  {quickTimes().map((q) => (
                    <button key={q.label} type="button" className="pe-tweak" onClick={() => { const v = utcToBrisbaneInputs(q.iso); setForm((f) => ({ ...f, ...v })); setDirty(true); }}>{q.label}</button>
                  ))}
                </div>
                <p className="text-muted pe-when-note">
                  {whenIso ? `Goes out ${whenLabel(whenIso)} (Gold Coast time) once approved.` : 'Mornings 7–8:30 and evenings 6–7:30 tend to do best.'}
                  {fbMode === 'simulate' && ' On this computer posts are simulated: nothing reaches Facebook.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {error && <div className="form-error pe-error">{error}</div>}
      </div>

      <div className="modal-actions pe-actions">
        {post.status !== 'publishing' && (
          confirm === 'delete' ? (
            <button type="button" className="btn btn-danger" disabled={busy === 'delete'} onClick={remove}>
              {post.status === 'published' ? 'Yes, take it off this list (it stays on Facebook)' : 'Yes, delete it'}
            </button>
          ) : (
            <button type="button" className="btn btn-secondary pe-delete" onClick={() => setConfirm('delete')}>{post.status === 'published' ? 'Remove from list' : 'Delete'}</button>
          )
        )}
        <span className="pe-spacer" />
        {post.status === 'idea' && (
          <>
            {dirty && <button type="button" className="btn btn-secondary" disabled={!!busy} onClick={() => save()}>Save</button>}
            <button type="button" className="btn btn-primary" disabled={!!busy} onClick={async () => { if (await save({ quiet: true })) simple('draft', contentService.fromIdea(post.id)); }}>
              {busy === 'draft' ? 'Setting it up…' : 'Make it a draft'}
            </button>
          </>
        )}
        {['draft', 'failed'].includes(post.status) && (
          <>
            <button type="button" className="btn btn-secondary" disabled={!dirty || !!busy} onClick={() => save()}>{busy === 'save' ? 'Saving…' : dirty ? 'Save' : 'Saved'}</button>
            {post.status === 'failed' && <button type="button" className="btn btn-outline" disabled={!!busy} onClick={() => simple('retry', contentService.retry(post.id))}>{busy === 'retry' ? 'Trying…' : 'Try again now'}</button>}
            {confirm === 'now' ? (
              <button type="button" className="btn btn-danger" disabled={!!busy} onClick={() => approve(true)}>{busy === 'now' ? 'Posting…' : 'Yes, post it now'}</button>
            ) : (
              <button type="button" className="btn btn-outline" disabled={!!busy} onClick={() => setConfirm('now')}>Post now</button>
            )}
            <button type="button" className="btn btn-primary" disabled={!!busy} onClick={() => approve(false)}>{busy === 'approve' ? 'Approving…' : 'Approve & schedule'}</button>
          </>
        )}
        {post.status === 'scheduled' && (
          <>
            <button type="button" className="btn btn-outline" disabled={!!busy} onClick={() => simple('unapprove', contentService.unapprove(post.id))}>Back to drafts</button>
            <button type="button" className="btn btn-primary" disabled={!dirty || !!busy} onClick={() => save()}>{busy === 'save' ? 'Saving…' : dirty ? 'Save (stays scheduled)' : 'Saved'}</button>
          </>
        )}
        {post.status === 'publishing' && <span className="text-muted">Going out to Facebook now…</span>}
        {post.status === 'published' && post.fb_permalink && (
          <a className="btn btn-primary" href={post.fb_permalink} target="_blank" rel="noreferrer">View on Facebook</a>
        )}
        <button type="button" className="btn btn-secondary pe-close" onClick={close}>Close</button>
      </div>

      {pictureOpen && (
        <Modal isOpen onClose={() => setPictureOpen(null)} title="✨ Make a picture for this post" size="xl">
          <PictureStudio postId={post.id} status={pictures} aiReady={aiReady} initialScene={pictureOpen.scene} castEditable={false} onUse={putPicture} />
          <div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setPictureOpen(null)}>Close</button></div>
        </Modal>
      )}
      <PhotoPicker isOpen={pickerOpen} onClose={() => setPickerOpen(false)} onAdd={addPhotos} onAddLibrary={addLibrary} onBeforeAfter={beforeAfter}
        focusJobId={post.job_id} defaultSource={post.job_id ? 'jobs' : 'library'} />
      {lightbox && <Lightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />}
    </Modal>
  );
}
